import { apiInstance } from '@diamond/shared/utils';

import type {
  BackofficeVoucherListResponse,
  BackofficeVoucherProductListQuery,
  BackofficeVoucherProductListResponse,
  CreateVoucherPayload,
  ShipToCsvUploadResponse,
  VoucherCheckCodePayload,
  VoucherCodeCheckResponse,
  VoucherDeleteApiResponse,
  VoucherDeletePayload,
  VoucherDetailResponse,
  VoucherFilters,
  VoucherReorderPayload,
  VoucherResponse,
  VoucherUpdateBasicInfoPayload,
  VoucherUpdateDisplayPayload,
  VoucherUpdateStatusPayload,
  VoucherUploadBannerResponse,
} from '../types';
import { MULTIPART_FORM_HEADERS, VOUCHER_API_ENDPOINTS } from './constants';
import { mapVoucherToListItem, sanitizeVoucherFilters } from './mappers';

export const getVouchers = async (
  token: string,
  filters?: VoucherFilters
): Promise<VoucherResponse> => {
  const response = await apiInstance(token).get<BackofficeVoucherListResponse>(
    VOUCHER_API_ENDPOINTS.LIST,
    {
      params: sanitizeVoucherFilters(filters),
    }
  );

  return {
    data: response.data.data.map(mapVoucherToListItem),
    pagination: response.data.pagination,
  };
};

export const getVoucher = async (
  token: string,
  id: string
): Promise<VoucherDetailResponse> => {
  const response = await apiInstance(token).get<VoucherDetailResponse>(
    VOUCHER_API_ENDPOINTS.DETAIL(id)
  );

  return response.data;
};

export const createVoucher = async (
  token: string,
  data: CreateVoucherPayload
) => {
  const response = await apiInstance(token).post(
    VOUCHER_API_ENDPOINTS.CREATE,
    data
  );
  return response.data;
};

export const updateVoucher = async (
  token: string,
  id: string,
  data: VoucherUpdateBasicInfoPayload
) => {
  const response = await apiInstance(token).patch(
    VOUCHER_API_ENDPOINTS.UPDATE(id),
    data
  );
  return response.data;
};

export const deleteVoucher = async (token: string, id: string) => {
  const payload: VoucherDeletePayload = { voucher_id: id };

  const { data } = await apiInstance(token).delete<VoucherDeleteApiResponse>(
    VOUCHER_API_ENDPOINTS.DELETE,
    {
      data: payload,
    }
  );

  if (!data.success) {
    throw new Error('Gagal menghapus voucher');
  }

  return data;
};

export const duplicateVoucher = async (token: string, id: string) => {
  const response = await apiInstance(token).post(
    VOUCHER_API_ENDPOINTS.DUPLICATE(id)
  );
  return response.data;
};

export const checkVoucherCode = async (token: string, code: string) => {
  const payload: VoucherCheckCodePayload = {
    voucher_code: code,
  };

  const response = await apiInstance(token).post<VoucherCodeCheckResponse>(
    VOUCHER_API_ENDPOINTS.CHECK_CODE,
    payload
  );

  return response.data;
};

export const uploadVoucherBanner = async (
  token: string,
  file: File
): Promise<VoucherUploadBannerResponse> => {
  const formData = new FormData();
  formData.append('file', file, file.name);

  const response = await apiInstance(token, {
    headers: MULTIPART_FORM_HEADERS,
  }).post<VoucherUploadBannerResponse>(
    VOUCHER_API_ENDPOINTS.UPLOAD_BANNER,
    formData
  );

  return response.data;
};

export const uploadShipToCsv = async (
  token: string,
  file: File
): Promise<ShipToCsvUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file, file.name);

  const response = await apiInstance(token, {
    headers: MULTIPART_FORM_HEADERS,
  }).post<ShipToCsvUploadResponse>(
    VOUCHER_API_ENDPOINTS.UPLOAD_SHIPTO_CSV,
    formData
  );

  return response.data;
};

export const reorderVoucher = async (
  token: string,
  id: string,
  newIndex: number
) => {
  const payload: VoucherReorderPayload = {
    index: newIndex,
  };

  await apiInstance(token).patch(VOUCHER_API_ENDPOINTS.REORDER(id), payload);
};

export const updateVoucherStatus = async (
  token: string,
  id: string,
  status: VoucherUpdateStatusPayload['status']
) => {
  const payload: VoucherUpdateStatusPayload = {
    voucher_id: id,
    status,
  };

  const response = await apiInstance(token).patch(
    VOUCHER_API_ENDPOINTS.UPDATE_STATUS,
    payload
  );

  return response.data;
};

export const updateVoucherDisplay = async (
  token: string,
  id: string,
  isDisplay: boolean
) => {
  const payload: VoucherUpdateDisplayPayload = {
    voucher_id: id,
    is_display: isDisplay,
  };

  const response = await apiInstance(token).patch(
    VOUCHER_API_ENDPOINTS.UPDATE_DISPLAY,
    payload
  );

  return response.data;
};

export const getVoucherProducts = async (
  token: string,
  params: BackofficeVoucherProductListQuery
): Promise<BackofficeVoucherProductListResponse> => {
  const response = await apiInstance(
    token
  ).get<BackofficeVoucherProductListResponse>(VOUCHER_API_ENDPOINTS.PRODUCTS, {
    params,
  });

  return response.data;
};
